import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { DisplayStages, StageName, StageType } from "../../../lib/types";
import { Icons } from "../../../components/ui/icons";

import toast from "react-hot-toast";

import { RootState, useAppSelector } from "../../../store";
import { safeParseInt } from "../../../components/Common/Util";
import "dayjs/locale/ko";
import { NumericFormat } from "react-number-format";
import { Input } from "components/ui/input";
import { Dialog, DialogContent, DialogHeader } from "components/ui/dialog";
import { Card, CardContent, CardTitle, CardHeader } from "components/ui/card";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Button } from "components/ui/button";
import DateTimePicker from "components/Common/datetime-picker";
import { Checkbox } from "components/ui/checkbox";
import { Label } from "components/ui/label";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
} from "components/ui/accordion";
import { AccordionContent } from "@radix-ui/react-accordion";
import { School, SchoolSales } from "lib/models";
import { apiPost } from "components/Common/Axios";
import { nowRoundDateToNearestFiveMinutes } from "lib/utils";

// interface Props {
//   open: boolean;
//   onOpenChange: (open: boolean) => void;
//   schoolSales: SchoolSales | undefined; // undefined면 학교를 클릭함(new) 아니면 영업정보를 클릭함(edit)
//   school: School;
//   onClose: () => void;
//   onSaveCompleted: (sales: SchoolSales, isNewMode: boolean) => void;
//   onDelete: (sales: SchoolSales) => Promise<boolean>;
//   isEnableNew: boolean;
//   isShowDelete: boolean;
//   accordionStates: string[];
// }

type DateType = "from" | "to" | "contract";

function SchoolSalesPopup({
  open,
  onOpenChange,
  schoolSales,
  school,
  onClose,
  onSaveCompleted,
  onDelete,
  isEnableNew,
  isShowDelete,
  accordionStates,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schoolSales: SchoolSales | undefined; // undefined면 학교를 클릭함(new) 아니면 영업정보를 클릭함(edit)
  school: School | undefined;
  onClose: () => void;
  onSaveCompleted: (sales: SchoolSales, isNewMode: boolean) => Promise<boolean>;
  onDelete: (sales: SchoolSales) => Promise<boolean>;
  isEnableNew: boolean;
  isShowDelete: boolean;
  accordionStates: string[];
}) {
  const [schoolSalesLocal, setSchoolSalesLocal] = useState<
    SchoolSales | undefined
  >(schoolSales);
  // const [openVatPopup, setOpenVatPopup] = useState(false);
  // const [openVatPopupMaterial, setOpenVatPopupMaterial] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(
    schoolSales !== undefined
  );
  const me = useAppSelector((state: RootState) => state.user.me);
  //const [accordionState] = useState(accordionStates);

  const handleSave = async () => {
    try {
      if (!schoolSalesLocal) {
        console.error("schoolSales is null", schoolSalesLocal);
        return;
      }

      const isNewMode = schoolSalesLocal.id === 0;

      if (schoolSalesLocal.stage >= StageType.CONTRACT_COMPLETED) {
        if (schoolSalesLocal.classType < 1) {
          toast.error("수업을 선택해주세요");
          return;
        }
        if (schoolSalesLocal.contractCnt < 1) {
          toast.error("계약학급 수를 입력해주세요");
          return;
        }
        if (schoolSalesLocal.studentCnt < 1) {
          toast.error("학생수를 입력해주세요");
          return;
        }
      }

      const fromDate = new Date(schoolSalesLocal.classDateFrom);
      const toDate = new Date(schoolSalesLocal.classDateTo);

      if (fromDate.getTime() > toDate.getTime()) {
        toast.error("수업 종료 일시가 시작 일시보다 빠를 수 없습니다");
        return;
      }

      setIsLoading(true);

      const {
        school,
        user,
        // vatCompany,
        // vatCompanyMaterial,
        vatInvoice,
        vatInvoiceMaterial,
        ...schoolSalesOnly
      } = schoolSalesLocal;

      let saveSchoolSales = { ...schoolSalesOnly };

      if (saveSchoolSales.vatInvoiceId === 0) {
        saveSchoolSales.vatInvoiceId = undefined;
      }
      if (saveSchoolSales.vatInvoiceMaterialId === 0) {
        saveSchoolSales.vatInvoiceMaterialId = undefined;
      }
      // 매출에 대한 입력이 완료되었는지 확인. "완"표시됨.
      saveSchoolSales.isRevenueInputCompleted =
        saveSchoolSales.lectureFee > 0 &&
        saveSchoolSales.cost > 0 &&
        saveSchoolSales.revenue > 0;

      const response = await apiPost("/school/sales", saveSchoolSales);
      if (response.error) {
        toast.error("저장 실패");
        console.error("저장 실패", response.error);
        return false;
      }
      const newSchoolSales = response.data as SchoolSales;

      await onSaveCompleted(newSchoolSales, isNewMode);

      toast.success("저장 성공");
      onClose();
    } catch (error: any) {
      console.error("handleSave", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!schoolSalesLocal) {
      return;
    }

    if (schoolSalesLocal.id === 0) {
      toast.error("삭제할 데이터가 없습니다.");
      return;
    }

    if (!window.confirm("삭제하시겠습니까?")) {
      return;
    }

    await onDelete(schoolSalesLocal);

    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  useEffect(() => {
    if (!school) {
      console.error("school is null", school);
      return;
    }
    if (isEnableNew && !schoolSales) {
      const newDate = new Date();
      const fromDate = nowRoundDateToNearestFiveMinutes();
      const toDate = new Date(fromDate.getTime());
      toDate.setHours(newDate.getHours() + 1);

      const sales = {
        id: 0,
        schoolId: school.id,
        userId: me.id,

        createDate: new Date(),
        memo: "",
        stage: 1,
        teacherName: "",
        phoneNo: "",
        email: "",

        classDateFrom: fromDate,
        classDateTo: toDate,
        classTimes: 3,
        contractDate: newDate,
        startPeriod: 1,
        endPeriod: 3,

        studentCnt: 20,
        grade: 1,
        calendarId: "",
        contractCnt: 5,
        classType: 1,

        revenue: 0,
        revenueLecture: 0,
        revenueMaterial: 0,
        lectureFee: 0,
        etcFee: 0,
        isIssuedLecture: false,
        isIssuedMaterial: false,
        isIssuedSeparate: false,
        isFeedbackCompleted: false,
        isSendSms: false,

        cost: 0,
        isExclude33: false,
        profit: 0,
        vatInvoiceId: 0,
        vatInvoiceMaterialId: 0,
        isRevenueInputCompleted: false,
        isVatInvoiceRequested: false,
      } as SchoolSales;

      setSchoolSalesLocal(sales);
    } else {
      setSchoolSalesLocal(schoolSales);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolSales, school, me.id]);

  useEffect(() => {
    setIsEditMode(schoolSales !== undefined);
  }, [schoolSales]);

  const onChangeStage = (strStage: string) => {
    if (!schoolSalesLocal) return;

    const stage = safeParseInt(strStage);
    setSchoolSalesLocal({ ...schoolSalesLocal, stage: stage });
  };

  const onChangeMemo = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!schoolSalesLocal) return;

    const memo = e.target.value;
    setSchoolSalesLocal({ ...schoolSalesLocal, memo: memo });
  };

  const onChangeTeacherName = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!schoolSalesLocal) return;

    const teacherName = e.target.value;
    setSchoolSalesLocal({ ...schoolSalesLocal, teacherName: teacherName });
  };

  const onChangePhoneNo = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!schoolSalesLocal) return;

    const phoneNo = e.target.value;
    setSchoolSalesLocal({ ...schoolSalesLocal, phoneNo: phoneNo });
  };

  const onChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!schoolSalesLocal) return;

    const email = e.target.value;
    setSchoolSalesLocal({ ...schoolSalesLocal, email: email });
  };

  const handleUpdateDate = (date: Date, dateType: DateType) => {
    if (!schoolSalesLocal) {
      console.error("schoolSales is null");
      return;
    }

    if (dateType === "from") {
      setSchoolSalesLocal({
        ...schoolSalesLocal,
        classDateFrom: date,
      });
    } else if (dateType === "to") {
      setSchoolSalesLocal({
        ...schoolSalesLocal,
        classDateTo: date,
      });
    } else {
      setSchoolSalesLocal({
        ...schoolSalesLocal,
        contractDate: date,
      });
    }
  };

  const handleClassTimesChange = (v: string) => {
    if (!schoolSalesLocal) return;

    const times = safeParseInt(v);
    setSchoolSalesLocal({ ...schoolSalesLocal, classTimes: times });
  };

  const handleStartPeriodChange = (v: string) => {
    if (!schoolSalesLocal) return;

    const startPeriod = safeParseInt(v);
    setSchoolSalesLocal({ ...schoolSalesLocal, startPeriod: startPeriod });
  };

  const handleEndPeriodChange = (v: string) => {
    if (!schoolSalesLocal) return;

    const endPeriod = safeParseInt(v);
    setSchoolSalesLocal({ ...schoolSalesLocal, endPeriod: endPeriod });
  };

  const handleStudentCntChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!schoolSalesLocal) return;

    const studentCnt = safeParseInt(e.target.value);
    setSchoolSalesLocal({ ...schoolSalesLocal, studentCnt });
  };

  const handleClassTypeChange = (value: string) => {
    setSchoolSalesLocal({
      ...schoolSalesLocal!,
      classType: safeParseInt(value),
    });
  };

  const handleContractCntChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!schoolSalesLocal) return;

    const contractCnt = safeParseInt(e.target.value);
    setSchoolSalesLocal({ ...schoolSalesLocal, contractCnt });
  };

  const handleGradeChange = (v: string) => {
    if (!schoolSalesLocal) return;

    const grade = safeParseInt(v);
    setSchoolSalesLocal({ ...schoolSalesLocal, grade });
  };

  useEffect(() => {
    if (!schoolSalesLocal) return;

    const lectureFee = schoolSalesLocal.isExclude33
      ? Math.round(schoolSalesLocal.lectureFee / 0.967)
      : schoolSalesLocal.lectureFee;

    const revenue =
      schoolSalesLocal.revenueLecture + schoolSalesLocal.revenueMaterial;
    const cost =
      lectureFee * schoolSalesLocal.contractCnt +
      (schoolSalesLocal.etcFee ?? 0);
    const profit = revenue - cost;

    setSchoolSalesLocal({
      ...schoolSalesLocal,
      revenue,
      profit,
      cost,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schoolSalesLocal?.revenueLecture,
    schoolSalesLocal?.revenueMaterial,
    schoolSalesLocal?.lectureFee,
    schoolSalesLocal?.etcFee,
    schoolSalesLocal?.contractCnt,
    schoolSalesLocal?.isExclude33,
  ]);

  // 강사매출 변경시
  const handleRevenueLectureChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!schoolSalesLocal) {
      console.error("schoolSalesLocal is null", schoolSalesLocal);
      return;
    }

    const revenueLecture = safeParseInt(e.target.value);
    setSchoolSalesLocal({
      ...schoolSalesLocal,
      revenueLecture,
    });
  };

  const handleRevenueMaterialChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!schoolSalesLocal) return;

    const revenueMaterial = safeParseInt(e.target.value);
    setSchoolSalesLocal({
      ...schoolSalesLocal,
      revenueMaterial,
    });
  };

  const handleLectureFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!schoolSalesLocal) return;

    const lectureFee = safeParseInt(e.target.value);
    setSchoolSalesLocal({
      ...schoolSalesLocal,
      lectureFee,
    });
  };

  const handleEtcFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!schoolSalesLocal) return;

    const etcFee = safeParseInt(e.target.value);
    setSchoolSalesLocal({
      ...schoolSalesLocal,
      etcFee,
    });
  };

  // const handleCostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   if (!schoolSalesLocal) return;

  //   const cost = safeParseInt(e.target.value);
  //   setSchoolSalesLocal({
  //     ...schoolSalesLocal,
  //     cost,
  //     profit: schoolSalesLocal.revenue - cost,
  //   });
  // };

  if (!schoolSalesLocal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-1rem)] max-w-[calc(100%-1rem)] md:max-w-screen-md max-h-[95%] overflow-x-hidden overflow-y-auto thin-scrollbar">
        <DialogHeader></DialogHeader>
        <Card className="border-0 shadow-none">
          <CardHeader className="px-0 pt-0 pb-3 mb-1 sm:px-2 border-b border-gray-100">
            <CardTitle className="text-xl sm:text-2xl font-bold text-center text-gray-900">영업정보</CardTitle>
          </CardHeader>
          <CardContent className="px-0 sm:px-2 flex flex-col gap-3">
            <Accordion
              type="multiple"
              className="w-full"
              defaultValue={accordionStates}
            >
              <AccordionItem value="item-1">
                <AccordionTrigger>
                  <div className="text-base font-semibold leading-none tracking-tight">
                    영업정보
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="text-sm flex flex-col gap-2">
                    <div className="flex gap-3 pb-3">
                      <div className="w-full sm:w-[120px] text-left sm:text-right">학교명</div>
                      <div className="pl-1 font-semibold">{school?.name}</div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start gap-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="w-full sm:w-[120px] text-left sm:text-right">단계</div>
                        <Select
                          value={schoolSalesLocal?.stage.toString() ?? "0"}
                          onValueChange={onChangeStage}
                        >
                          <SelectTrigger className="w-[150px] text-center">
                            <SelectValue placeholder="1" />
                          </SelectTrigger>
                          <SelectContent className="w-[150px]">
                            <SelectGroup>
                              {DisplayStages.map((stage, index) => (
                                <SelectItem
                                  key={index}
                                  value={stage.toString()}
                                >
                                  {StageName[stage]}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="w-full sm:w-[150px] text-left sm:text-right">이메일</div>
                        <Input
                          type="text"
                          value={schoolSalesLocal?.email ?? ""}
                          onChange={onChangeEmail}
                          className="w-full sm:w-[200px]"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-start gap-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="w-full sm:w-[120px] text-left sm:text-right">담당선생님</div>
                        <Input
                          type="text"
                          value={schoolSalesLocal?.teacherName ?? ""}
                          onChange={onChangeTeacherName}
                          className="w-full sm:w-[200px]"
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="w-full sm:w-[100px] text-left sm:text-right">휴대전화</div>
                        <Input
                          type="text"
                          value={schoolSalesLocal?.phoneNo ?? ""}
                          onChange={onChangePhoneNo}
                          className="w-full sm:w-[200px]"
                        />
                      </div>
                    </div>
                    <div className="sm:pl-[140px] text-xs text-indigo-500 text-left sm:text-right">
                      수업완료 후 수업결과 페이지를 입력한 휴대전화 번호로
                      문자로 전송합니다.
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] items-start sm:items-center gap-1 sm:gap-3">
                      <div className="text-left sm:text-right">메모</div>
                      <textarea
                        className="h-20 border border-solid border-gray-300 p-2"
                        value={schoolSalesLocal?.memo ?? ""}
                        onChange={onChangeMemo}
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger>
                  <div className="text-base font-semibold leading-none tracking-tight">
                    수업정보
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="text-sm flex flex-col gap-2 ">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="w-full sm:w-[120px] text-left sm:text-right">수업</div>
                      <Select
                        value={schoolSalesLocal?.classType?.toString() ?? "0"}
                        onValueChange={handleClassTypeChange}
                      >
                        <SelectTrigger className="w-[150px] text-center">
                          <SelectValue placeholder="1" />
                        </SelectTrigger>
                        <SelectContent className="w-[150px]">
                          <SelectGroup>
                            <SelectItem value="0">선택안함</SelectItem>
                            {/* 수업추가시 */}
                            <SelectItem value="1">창업</SelectItem>
                            <SelectItem value="2">창직</SelectItem>
                            <SelectItem value="3">인공지능</SelectItem>
                            <SelectItem value="4">경제</SelectItem>
                            <SelectItem value="5">진학</SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <div className="w-full sm:w-[100px] text-left sm:text-right">학급수</div>
                      <div>
                        <Input
                          className="w-[150px] text-center"
                          type="text"
                          value={
                            schoolSalesLocal?.contractCnt?.toString() ?? ""
                          }
                          onChange={handleContractCntChange}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="w-full sm:w-[120px] text-left sm:text-right">수업교시 수</div>
                      <Select
                        value={schoolSalesLocal?.classTimes?.toString() ?? ""}
                        onValueChange={handleClassTimesChange}
                      >
                        <SelectTrigger className="w-[150px] text-center">
                          <SelectValue placeholder="1" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {Array.from({ length: 10 }, (_, i) => i + 1).map(
                              (item, index) => (
                                <SelectItem key={index} value={item.toString()}>
                                  {item}교시
                                </SelectItem>
                              )
                            )}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <div className="w-full sm:w-[100px] text-left sm:text-right">수업교시</div>
                      <Select
                        value={schoolSalesLocal?.startPeriod?.toString() ?? ""}
                        onValueChange={handleStartPeriodChange}
                      >
                        <SelectTrigger className="w-[100px] text-center">
                          <SelectValue placeholder="1" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {Array.from({ length: 10 }, (_, i) => i + 1).map(
                              (item, index) => (
                                <SelectItem key={index} value={item.toString()}>
                                  {item}교시
                                </SelectItem>
                              )
                            )}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <div className="text-center p-1"> ~ </div>
                      <Select
                        value={schoolSalesLocal?.endPeriod?.toString() ?? ""}
                        onValueChange={handleEndPeriodChange}
                      >
                        <SelectTrigger className="w-[100px] text-center">
                          <SelectValue placeholder="1" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {Array.from({ length: 10 }, (_, i) => i + 1).map(
                              (item, index) => (
                                <SelectItem key={index} value={item.toString()}>
                                  {item}교시
                                </SelectItem>
                              )
                            )}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="w-full sm:w-[120px] text-left sm:text-right">학년</div>
                      <Select
                        value={schoolSalesLocal?.grade?.toString() ?? "1"}
                        onValueChange={handleGradeChange}
                      >
                        <SelectTrigger className="w-[150px] text-center">
                          <SelectValue placeholder="1" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {Array.from({ length: 6 }, (_, i) => i + 1).map(
                              (item, index) => (
                                <SelectItem key={index} value={item.toString()}>
                                  {item}학년
                                </SelectItem>
                              )
                            )}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <div className="w-full sm:w-[100px] text-left sm:text-right">학생수</div>
                      <div>
                        <Input
                          className="w-[100px] text-center"
                          type="text"
                          value={schoolSalesLocal?.studentCnt?.toString() ?? ""}
                          onChange={handleStudentCntChange}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="w-full sm:w-[120px] text-left sm:text-right">계약일</div>
                      <div className="w-full sm:w-[200px] text-sm">
                        <DateTimePicker
                          name="contractDate"
                          datetime={schoolSalesLocal?.contractDate}
                          setDatetime={(date) =>
                            handleUpdateDate(date, "contract")
                          }
                          isShowTime={false}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="w-full sm:w-[120px] text-left sm:text-right">수업일</div>
                      <div className="flex flex-col gap-3 text-sm">
                        <DateTimePicker
                          name="classDateFrom"
                          datetime={schoolSalesLocal?.classDateFrom}
                          setDatetime={(date) => handleUpdateDate(date, "from")}
                        />
                        <DateTimePicker
                          name="classDateTo"
                          datetime={schoolSalesLocal?.classDateTo}
                          setDatetime={(date) => handleUpdateDate(date, "to")}
                        />
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger>
                  <div className="text-base font-semibold leading-none tracking-tight">
                    매출정보
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="text-sm flex flex-col gap-2 ">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="w-full sm:w-[120px] text-left sm:text-right">매출</div>
                        <div className="w-full sm:w-[150px]">
                          <NumericFormat
                            value={schoolSalesLocal?.revenue ?? 0}
                            thousandSeparator=","
                            allowLeadingZeros={false}
                            readOnly
                            className="text-right border border-solid border-gray-200 rounded-lg p-2 pr-4 w-full text-gray-400"
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="w-full sm:w-[100px] text-left sm:text-right">비용</div>
                        <div className="w-full sm:w-[150px]">
                          <NumericFormat
                            value={schoolSalesLocal?.cost ?? 0}
                            thousandSeparator=","
                            allowLeadingZeros={false}
                            //onChange={(e) => handleCostChange(e)}
                            className="text-right border border-solid border-gray-300 rounded-lg p-2 pr-4 w-full text-gray-400"
                            onFocus={(e) => e.target.select()}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="w-full sm:w-[120px] text-left sm:text-right">강사료 매출</div>
                        <div className="w-full sm:w-[150px]">
                          <NumericFormat
                            value={schoolSalesLocal?.revenueLecture ?? 0}
                            thousandSeparator=","
                            allowLeadingZeros={false}
                            onChange={(e) => handleRevenueLectureChange(e)}
                            className="text-right border border-solid border-gray-300 rounded-lg p-2 pr-4 w-full read-only:text-gray-400"
                            onFocus={(e) => e.target.select()}
                            readOnly={schoolSalesLocal?.isFree ?? false}
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="w-full sm:w-[100px] text-left sm:text-right">지급강사비</div>
                        <div className="w-full sm:w-[150px] flex items-center">
                          <NumericFormat
                            value={schoolSalesLocal?.lectureFee ?? 0}
                            onChange={(e) => handleLectureFeeChange(e)}
                            thousandSeparator=","
                            allowLeadingZeros={false}
                            className="text-right border border-solid border-gray-300 rounded-lg p-2 pr-4 w-full read-only:text-gray-400"
                            readOnly={schoolSalesLocal?.isFree ?? false}
                          />
                        </div>
                        <Checkbox
                          id="checkIsExclude33"
                          className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600 read-only:border-gray-300"
                          checked={schoolSalesLocal?.isExclude33 ?? false}
                          onCheckedChange={(v: boolean) =>
                            setSchoolSalesLocal({
                              ...schoolSalesLocal,
                              isExclude33: v,
                            })
                          }
                          disabled={schoolSalesLocal?.isFree ?? false}
                        />
                        <Label
                          htmlFor="checkIsExclude33"
                          className="read-only:text-gray-400"
                        >
                          세후금액
                        </Label>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="w-full sm:w-[120px] flex items-center sm:justify-end gap-x-1">
                          재료비 매출
                        </div>
                        <div className="w-full sm:w-[150px]">
                          <NumericFormat
                            value={schoolSalesLocal?.revenueMaterial ?? 0}
                            thousandSeparator=","
                            allowLeadingZeros={false}
                            onChange={(e) => handleRevenueMaterialChange(e)}
                            className="text-right border border-solid border-gray-300 rounded-lg p-2 pr-4 w-full read-only:text-gray-400"
                            onFocus={(e) => e.target.select()}
                            readOnly={schoolSalesLocal?.isFree ?? false}
                          />
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="w-full sm:w-[100px] text-left sm:text-right">기타비용</div>
                        <div className="w-full sm:w-[150px] flex items-center">
                          <NumericFormat
                            value={schoolSalesLocal?.etcFee ?? 0}
                            onChange={(e) => handleEtcFeeChange(e)}
                            thousandSeparator=","
                            allowLeadingZeros={false}
                            className="text-right border border-solid border-gray-300 rounded-lg p-2 pr-4 w-full read-only:text-gray-400"
                            readOnly={schoolSalesLocal?.isFree ?? false}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 pt-2">
                      <div className="hidden sm:block w-[120px]"></div>
                      <Checkbox
                        id="checkIsFree"
                        className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                        checked={schoolSalesLocal?.isFree ?? false}
                        onCheckedChange={(v: boolean) => {
                          if (v) {
                            setSchoolSalesLocal({
                              ...schoolSalesLocal,
                              revenueLecture: 0,
                              revenueMaterial: 0,
                              lectureFee: 0,
                              etcFee: 0,
                              isExclude33: false,
                              isFree: true,
                            });
                          } else {
                            setSchoolSalesLocal({
                              ...schoolSalesLocal,
                              isFree: false,
                            });
                          }
                        }}
                      />
                      <Label htmlFor="checkIsFree">무료 수업</Label>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="flex flex-wrap justify-between gap-3 pt-8">
              {isEditMode && isShowDelete ? (
                <Button
                  size="lg"
                  variant="destructive"
                  onClick={() => handleDelete()}
                >
                  삭제
                </Button>
              ) : (
                <div></div>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={() => handleSave()}
                  className="flex justify-center items-center"
                  size="lg"
                  variant="primary"
                >
                  {isLoading && (
                    // eslint-disable-next-line react/jsx-pascal-case
                    <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  <div>저장</div>
                </Button>
                <Button size="lg" variant="secondary" onClick={handleCancel}>
                  취소
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
}

export default SchoolSalesPopup;
